/**
 * Collaboration room naming and document storage-format contracts.
 *
 * A `legacy-single-v1` document keeps its whole Y.Doc state in one room named
 * `{documentId}`. A `sectioned-v1` document keeps an ordered subdocument
 * manifest in `{documentId}` and every section body in its own room named
 * `{documentId}:section:{sectionId}`.
 *
 * Both the Hocuspocus authorization path and the frontend provider lifecycle
 * derive room membership from these helpers, so the parsing rules must stay
 * strict: a room name is never trusted input.
 */

export const LEGACY_COLLABORATION_FORMAT = "legacy-single-v1" as const;
export const SECTIONED_COLLABORATION_FORMAT = "sectioned-v1" as const;

export type CollaborationFormat =
  | typeof LEGACY_COLLABORATION_FORMAT
  | typeof SECTIONED_COLLABORATION_FORMAT;

export function isCollaborationFormat(value: unknown): value is CollaborationFormat {
  return value === LEGACY_COLLABORATION_FORMAT || value === SECTIONED_COLLABORATION_FORMAT;
}

/** Separator between the parent document UUID and the section UUID. */
export const SECTION_ROOM_SEPARATOR = ":section:";

// Canonical RFC 4122 UUID (variant bits 89ab). The version nibble is not
// restricted: IAM issues v7 identities while legacy rows carry v4. Route
// validation and room parsing must accept exactly the same shape, so this is the
// single definition both sides import.
export const CANONICAL_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type CollaborationRoom =
  | { kind: "document"; documentId: string }
  | { kind: "section"; documentId: string; sectionId: string };

/** Thrown for any room name that does not match a supported room shape. */
export class InvalidCollaborationRoomNameError extends Error {
  constructor(reason: string) {
    super(`Invalid collaboration room name: ${reason}`);
    this.name = "InvalidCollaborationRoomNameError";
  }
}

/** Builds the section room name for a section that belongs to `documentId`. */
export function sectionRoomName(documentId: string, sectionId: string): string {
  assertUuid(documentId, "documentId");
  assertUuid(sectionId, "sectionId");
  return `${documentId}${SECTION_ROOM_SEPARATOR}${sectionId}`;
}

/**
 * Parses a Hocuspocus room name into the parent document and optional section
 * it addresses. Callers still have to verify DB ownership of the section: this
 * only guarantees the name is well formed.
 */
export function parseCollaborationRoomName(roomName: string): CollaborationRoom {
  if (!roomName.includes(SECTION_ROOM_SEPARATOR)) {
    assertUuid(roomName, "documentId");
    return { kind: "document", documentId: roomName };
  }

  const tokens = roomName.split(SECTION_ROOM_SEPARATOR);
  if (tokens.length !== 2) {
    throw new InvalidCollaborationRoomNameError(
      `expected exactly one "${SECTION_ROOM_SEPARATOR}" separator`
    );
  }

  const [documentId, sectionId] = tokens;
  assertUuid(documentId, "documentId");
  assertUuid(sectionId, "sectionId");
  return { kind: "section", documentId, sectionId };
}

function assertUuid(value: string, field: "documentId" | "sectionId"): void {
  if (!CANONICAL_UUID.test(value)) {
    throw new InvalidCollaborationRoomNameError(`${field} is not a canonical UUID`);
  }
}
